import { buildGoogleCalendarLink, buildInterviewMeetingLink } from '../../utils/interviewScheduling';
import { buildMinimalEmail } from '../../utils/emailTemplates';

export const EMAIL_API_URL = 'https://us-central1-ubunifu-college.cloudfunctions.net/api/send-email';
export const SMS_API_URL = 'https://us-central1-ubunifu-college.cloudfunctions.net/api/send-sms';
export const LAB_REMINDER_LOGS_COLLECTION = 'LabCallReminders';

export const getDateObject = (value, time = '00:00') => {
  if (!value) return null;

  const parsed = new Date(`${value}T${time || '00:00'}:00`);
  if (Number.isNaN(parsed.getTime())) return null;

  return parsed;
};

export const formatDateTime = (date, time) => {
  const parsed = getDateObject(date, time);
  if (!parsed) return `${date || 'Date TBD'} ${time || ''}`.trim();

  return parsed.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

export const formatCreatedAt = (value) => {
  if (!value) return 'Recently added';

  const parsed = value?.toDate ? value.toDate() : value?.seconds ? new Date(value.seconds * 1000) : new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Recently added';

  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

export const getCallStatus = (call) => {
  const scheduledFor = getDateObject(call?.date, call?.time);
  if (!scheduledFor) {
    return {
      label: 'Details received',
      tone: 'neutral',
      needsReminder: false
    };
  }

  const now = new Date();
  const diffMs = scheduledFor.getTime() - now.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);
  const isSameDate = scheduledFor.toDateString() === now.toDateString();

  if (diffMs < 0) {
    return {
      label: 'Past call',
      tone: 'muted',
      needsReminder: false
    };
  }

  if (isSameDate) {
    return {
      label: 'Today',
      tone: 'today',
      needsReminder: true
    };
  }

  if (diffHours <= 24) {
    return {
      label: 'Reminder due',
      tone: 'warning',
      needsReminder: true
    };
  }

  return {
    label: 'Upcoming',
    tone: 'upcoming',
    needsReminder: false
  };
};

export const formatReminderLabel = (value) => {
  if (value === 'confirmation') return 'Booking confirmation';
  if (value === 'pre_call') return 'Pre-call reminder';
  return 'Reminder';
};

export const getLabMeetingLink = (call) => {
  const roomId = String(call?.roomId || '').trim();
  if (roomId) {
    return buildInterviewMeetingLink(roomId);
  }

  return String(call?.roomUrl || '').trim();
};

export const buildReminderEmailHtml = (call) => {
  const meetingLink = getLabMeetingLink(call);

  return buildMinimalEmail({
    eyebrow: 'Ubunifu Lab reminder',
    title: `Hi ${call.name || 'there'}, your session is coming up.`,
    intro: `This is a reminder about your ${call.help || 'product session'} call with the Ubunifu Lab team.`,
    details: [
      { label: 'Session', value: call.help || 'Product session' },
      { label: 'Date and time', value: formatDateTime(call.date, call.time) },
      { label: 'Meeting link', value: meetingLink || 'Link will be shared shortly' }
    ],
    bodyHtml: `
      <p style="margin:0;color:#667085;font-size:14px;line-height:1.7;">Please keep a few minutes free before the session so we can start on time.</p>
    `,
    primaryCta: meetingLink ? { href: meetingLink, label: 'Join meeting' } : undefined
  });
};

export const buildConfirmationEmailHtml = (call) => {
  const meetingLink = getLabMeetingLink(call);

  return buildMinimalEmail({
    eyebrow: 'Ubunifu Lab booking',
    title: `Hi ${call.name || 'there'}, your session is booked.`,
    intro: `Your ${call.help || 'product discovery'} session with the Ubunifu Lab team has been booked successfully.`,
    details: [
      { label: 'Session', value: call.help || 'Product session' },
      { label: 'Date and time', value: formatDateTime(call.date, call.time) },
      { label: 'Meeting link', value: meetingLink || 'Link will be shared shortly' }
    ],
    bodyHtml: `
      <p style="margin:0;color:#667085;font-size:14px;line-height:1.7;">Come ready to walk us through your idea, goals, and any blockers you want the team to solve with you.</p>
    `,
    primaryCta: meetingLink ? { href: meetingLink, label: 'Join meeting' } : undefined
  });
};

export const buildReminderSms = (call) => (
  `Hi ${call.name || ''}, this is a reminder for your ${call.help || 'product session'} call with Ubunifu on `
  + `${formatDateTime(call.date, call.time)}. Join here: ${getLabMeetingLink(call) || ''}`
).trim();

export const buildConfirmationSms = (call) => (
  `Hi ${call.name || ''}, your ${call.help || 'product session'} with Ubunifu has been booked for `
  + `${formatDateTime(call.date, call.time)}. Meeting link: ${getLabMeetingLink(call) || ''}`
).trim();

export const formatDeliverySummary = (log) => {
  const emailSuccess = Boolean(log?.delivery?.email?.success);
  const smsSuccess = Boolean(log?.delivery?.sms?.success);

  if (emailSuccess && smsSuccess) return 'Email + SMS sent';
  if (emailSuccess) return 'Email sent';
  if (smsSuccess) return 'SMS sent';
  return 'Delivery failed';
};

export const getCalendarLink = (call) => {
  const start = getDateObject(call?.date, call?.time);
  if (!start) return '';

  const end = new Date(start.getTime() + (45 * 60 * 1000));

  return buildGoogleCalendarLink({
    title: `${call?.help || 'Product session'} with ${call?.name || 'client'}`,
    start,
    end,
    details: `Client: ${call?.name || ''}\nEmail: ${call?.email || ''}\nPhone: ${call?.phone || ''}\nRoom: ${getLabMeetingLink(call) || ''}`,
    location: getLabMeetingLink(call) || 'Online meeting'
  });
};
